import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCompanyMutationsQuery, useStagePendingSignupGrant } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Input, TextArea } from '@repo/ui'
import { SelectField } from 'components'
import type { Company } from 'types'

const DURATION_OPTIONS = [
  { label: '1 month', value: '1' },
  { label: '3 months', value: '3' },
  { label: '6 months', value: '6' },
  { label: 'Custom', value: 'custom' },
]

const MIN_CUSTOM_DURATION_MONTHS = 1
const MAX_CUSTOM_DURATION_MONTHS = 24

interface CompSubscriptionModalProps {
  /** An existing company — comps it directly. */
  company?: Company | null
  /**
   * VRG-247 (Waitlist pre-load, Batch 4b): a Waitlist entry that has no
   * company yet — stages the comp instead, applied automatically once a
   * company is created for this signup. Mutually exclusive with `company`.
   */
  signupId?: string
  /**
   * VRG-545: an ALLOWLIST entry — allowed to sign up, no company and possibly
   * no waitlist row either. Stages the comp against the signups row the API
   * resolves (or creates) for this address. Mutually exclusive with `company`
   * and `signupId`.
   */
  email?: string
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const CompSubscriptionModal = (props: CompSubscriptionModalProps) => {
  const { company, signupId, email, open, onClose = () => {}, refetch } = props

  // Staging (no company yet) vs comping a live subscription. Both pre-company
  // entry points read the same to the user — the copy and the confirm verb
  // follow this, not the particular key used to resolve the row.
  const isStaging = !company?.id && (!!signupId || !!email)

  const { open: notification } = useSnackbar()

  const [durationMonths, setDurationMonths] = useState('')
  const [customDurationMonths, setCustomDurationMonths] = useState('')
  const [reason, setReason] = useState('')

  const isCustomDuration = durationMonths === 'custom'
  const parsedCustomDuration = Number(customDurationMonths)
  const isValidCustomDuration =
    customDurationMonths !== '' &&
    Number.isInteger(parsedCustomDuration) &&
    parsedCustomDuration >= MIN_CUSTOM_DURATION_MONTHS &&
    parsedCustomDuration <= MAX_CUSTOM_DURATION_MONTHS

  const effectiveDurationMonths = isCustomDuration
    ? (isValidCustomDuration ? parsedCustomDuration : null)
    : (durationMonths ? Number(durationMonths) : null)

  const isValidDuration = effectiveDurationMonths !== null

  const { compSubscription, isCompingSubscription } = useCompanyMutationsQuery({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Subscription comped.',
      })
      handleClose()
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const { mutate: stagePendingGrant, isPending: isStagingComp } = useStagePendingSignupGrant({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Comp staged. It will apply once this signup becomes a company.',
      })
      handleClose()
      refetch()
    },
    onError: error => {
      notification({
        type: 'error',
        title: error?.message || 'Failed to stage comp',
      })
    },
  })

  const isSubmitting = isCompingSubscription || isStagingComp

  const handleConfirm = () => {
    if (!isValidDuration || effectiveDurationMonths === null || !reason) return
    if (company?.id) {
      compSubscription({
        companyId: company.id,
        durationMonths: effectiveDurationMonths,
        reason,
      })
    } else if (signupId) {
      stagePendingGrant({
        signupId,
        compDurationMonths: effectiveDurationMonths,
        compReason: reason,
      })
    } else if (email) {
      stagePendingGrant({
        email,
        compDurationMonths: effectiveDurationMonths,
        compReason: reason,
      })
    }
  }

  const handleClose = () => {
    setDurationMonths('')
    setCustomDurationMonths('')
    setReason('')
    onClose()
  }

  return (
    <Modal
      title="Comp subscription"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            onClick={handleConfirm}
            disabled={isSubmitting || !isValidDuration || !reason}
          >
            {isSubmitting ? 'Saving...' : isStaging ? 'Stage comp' : 'Comp subscription'}
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {isStaging
            ? "This company doesn't exist yet. The comp is staged now and applies automatically as soon as this signup becomes a company. Tag orders and certificate activations still bill normally."
            : 'The base subscription fee is waived for the selected duration. Tag orders and certificate activations continue to bill normally.'}
        </Typography>

        <SelectField
          label="Duration"
          value={durationMonths}
          onChange={v => {
            setDurationMonths(v as string)
            if (v !== 'custom') setCustomDurationMonths('')
          }}
          options={DURATION_OPTIONS}
          placeholder="Select duration"
        />

        {isCustomDuration && (
          <Input
            label="Custom duration (months)"
            type="number"
            placeholder="e.g. 12"
            value={customDurationMonths}
            onValueChange={setCustomDurationMonths}
            error={customDurationMonths !== '' && !isValidCustomDuration}
            helperText={`Whole months, ${MIN_CUSTOM_DURATION_MONTHS}-${MAX_CUSTOM_DURATION_MONTHS}`}
          />
        )}

        <TextArea
          label="Reason"
          placeholder="Why is this subscription being comped?"
          value={reason}
          onValueChange={setReason}
          minRows={2}
        />
      </Stack>
    </Modal>
  )
}
